import * as yup from 'yup';
import { ItemScheme } from '../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../Enums/MagicField.enum';

const nonKeyWhiteList = ['condition', 'hstack', 'container'];

interface InputValidations {
	[MagicField.PRICE_WITH_CURRENCY]: (
		inputName: string,
		isRequired?: boolean,
		allowZero?: boolean,
	) => yup.AnySchema;
}

const loopItems = ({
	items,
	returnObject = false,
	inputValidations,
	t,
}: {
	items: ItemScheme[];
	returnObject?: boolean;
	inputValidations: InputValidations;
	t: (key: string) => string | undefined;
}) => {
	let data = {};

	for (const item of items) {
		const { key, type, label, title } = item;
		const fieldName = label || title || '';

		if (!key && nonKeyWhiteList.indexOf(type) === -1) {
			continue;
		}

		if (
			['radio_list', 'checkbox_list', 'select'].indexOf(item.type) === -1 &&
			item?.children?.length
		) {
			if (item.type === 'multiplier') {
				// @ts-ignore
				data[key] = yup.array().of(
					loopItems({
						items: item.children,
						returnObject: false,
						inputValidations,
						t,
					}),
				);
			} else if (item.type === 'condition') {
				const conditionChildren = loopItems({
					items: item.children,
					returnObject: true,
					inputValidations,
					t,
				});
				const parsed = {};
				for (const conditionChildKey in conditionChildren) {
					if (item?.settings?.rules) {
						// @ts-ignore
						parsed[conditionChildKey] = yup.mixed();

						for (const rule of item?.settings?.rules) {
							let formControlKey = rule.formControlKey.split('.');
							formControlKey = formControlKey[formControlKey.length - 1];

							// @ts-ignore
							parsed[conditionChildKey] = parsed[conditionChildKey].when(
								formControlKey,
								{
									is: (itemValue: any) => {
										const realValue = itemValue?.value ?? itemValue;
										switch (rule.rule) {
											case 'equal':
												return realValue === rule.value;
											case 'notEqual':
												return realValue !== rule.value;
											case 'in':
												return rule.value.indexOf(realValue) !== -1;
										}

										return false;
									},
									// @ts-ignore
									then: conditionChildren[conditionChildKey],
								},
							);
						}
					} else {
						let formControlKey = item?.value?.toString()?.split('.') || [];
						// @ts-ignore
						formControlKey = formControlKey[formControlKey.length - 1];

						// @ts-ignore
						parsed[conditionChildKey] = yup.mixed().when(formControlKey, {
							is: (val: any) => {
								const { equal, in: pin } = item?.settings || {};

								if (equal) {
									return val === equal;
								}

								if (pin) {
									return pin.indexOf(val) !== -1;
								}

								return false;
							},
							then: conditionChildren[conditionChildKey],
						});
					}
				}

				data = {
					...data,
					...parsed,
				};
			} else if (item.type === 'hstack') {
				const conditionChildren = loopItems({
					items: item.children,
					returnObject: true,
					inputValidations,
					t,
				});

				data = {
					...data,
					...conditionChildren,
				};
			} else if (item.type === 'container' && !key) {
				const containerChildren = loopItems({
					items: item.children,
					returnObject: true,
					inputValidations,
					t,
				});

				data = {
					...data,
					...containerChildren,
				};
			} else {
				data[key] = yup.object().shape(
					loopItems({
						items: item.children,
						returnObject: true,
						inputValidations,
						t,
					}),
				);

				if (item?.settings?.required) {
					data[key] = data[key].required();
				}
			}
		} else {
			let yupItem;

			switch (item.type) {
				case MagicField.PRICE_WITH_CURRENCY:
					yupItem = inputValidations[MagicField.PRICE_WITH_CURRENCY](
						item.title ?? '',
						false,
						item?.settings?.allowZero,
					).nullable();
					break;
				case MagicField.TEXT_FIELD:
					if (item?.subType === 'number') {
						yupItem = yup
							.number()
							.nullable()
							.typeError('validation.form.mustFillNumber');
					} else {
						yupItem = yup.string().nullable();
					}
					for (const rule of item?.settings?.rules || []) {
						if (rule.type === 'maxLength') {
							yupItem = yupItem.max(rule.value, rule.message);
						}
					}
					break;
				case MagicField.PHONE_NUMBER:
					yupItem = yup.string().nullable();
					break;
				case MagicField.CHECKBOX:
					yupItem = yup.boolean();
					break;
				case MagicField.DATEPICKER:
					yupItem = yup.mixed().nullable();
					break;
				case MagicField.SELECT:
				case MagicField.DOCTOR_SELECT:
				case MagicField.PATIENT_SELECT:
				case MagicField.QUESTIONNAIRE_SELECT:
				case MagicField.ORGANIZATION_UNIT_SELECT:
				case MagicField.VAT_RATE:
				case MagicField.QUESTIONNAIRE_SCHEDULE_FREQUENCY:
					yupItem = yup.mixed().nullable().default(undefined);
					break;
				case MagicField.RADIOLIST:
					const radioListValues =
						item?.children?.map(child => child?.value) || [];

					// console.log({key, radioListValues})

					if (!item?.settings?.required) {
						radioListValues.push(null);
					}

					yupItem = yup.mixed().oneOf(radioListValues).nullable().default(null);
					break;
				case MagicField.EMAIL:
					yupItem = yup.string().email('validation.form.email');
					break;
				case MagicField.TEXTAREA:
					yupItem = yup.string().nullable();
					break;
			}

			if (yupItem) {
				if (item?.settings?.required) {
					switch (item.type) {
						case MagicField.SELECT:
						case MagicField.DOCTOR_SELECT:
						case MagicField.PATIENT_SELECT:
						case MagicField.QUESTIONNAIRE_SELECT:
						case MagicField.ORGANIZATION_UNIT_SELECT:
						case MagicField.VAT_RATE:
							yupItem = yupItem
								.clone()
								.test(
									'is-required-select',
									item.settings?.errorMessage ??
										`${t(fieldName)} ${
											t('validation.form.isRequiredToFill') ?? ''
										}`,
									(value, testContext) => {
										const realVal =
											typeof value === 'object' ? value?.value : value;
										return realVal !== null && typeof realVal !== 'undefined';
									},
								);
							break;
						case MagicField.TEXT_FIELD:
							yupItem = yupItem
								.clone()
								.required(
									item.settings?.errorMessage ??
										`${t(fieldName)} ${
											t('validation.form.isRequiredToFill') ?? ''
										}`,
								);
							break;
						case MagicField.RADIOLIST:
							yupItem = yupItem
								.clone()
								.required(
									item.settings?.errorMessage ??
										`${t(fieldName)} ${
											t('validation.form.isRequiredToFill') ?? ''
										}`,
								);
							break;
						default:
							yupItem = yupItem
								.clone()
								.required(
									item.settings?.errorMessage ??
										`${t(fieldName)} ${
											t('validation.form.isRequiredToFill') ?? ''
										}`,
								);
							break;
					}
				} else {
					yupItem = yupItem.clone().nullable();
				}
			}

			data[key] = yupItem;
		}
	}

	if (returnObject) {
		return data;
	}

	return yup.object(data);
};

export const getValidationScheme = (
	data: ItemScheme[],
	inputValidations: InputValidations,
	t: (key: string) => string | undefined,
) => {
	return loopItems({ items: data, inputValidations, t });
};
